import { useFormik } from "formik";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useDrawMapStore, useMapShapeStore, usePipedriveStore, useTenantInMarketStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useRowClassName from "../../hooks/useRowClassName";
import useTenantInMarketDelete from "../../hooks/useTenantInMarketDelete";
import useTenantInMarketUpdate from "../../hooks/useTenantInMarketUpdate";
import useTenantInMarketValidationSchema from "../../hooks/useTenantInMarketValidationSchema";
import DetailsType from "../../interfaces/DetailsType";
import { ITenantInMarketState, TenantInMarketState } from "../../interfaces/ITenantInMarket";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import editStyles from "../Edit.module.css";
import EditControls, { DeleteControl, MarkInactiveControl } from "../EditControls";
import TenantInMarketIcon from "../icons/TenantInMarketIcon";
import StringInput from "../inputs/StringInput";
import OnMapSpinner from "../spinners/OnMapSpinner";
import TenantInMarketFields from "./TenantInMarketFields";

function TenantInMarketEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const updateTenantInMarket = useTenantInMarketUpdate();
    const deleteTenantInMarket = useTenantInMarketDelete();
    const tenantInMarketStore = useTenantInMarketStore();
    const pipedriveStore = usePipedriveStore();
    const drawMapStore = useDrawMapStore();
    const mapShapeStore = useMapShapeStore();
    const tenantInMarket = useMemo(() => computed(() => tenantInMarketStore.tenantsInMarket.find(tim => tim.id.toString() === id)), [id]).get();
    const companyPipedriveOrganization = useMemo(() => computed(() => tenantInMarket && pipedriveStore.organizations.find(o => o.id === tenantInMarket.pipedriveCompanyId)), [tenantInMarket]).get();
    const brokeragePipedriveOrganization = useMemo(() => computed(() => tenantInMarket && pipedriveStore.organizations.find(o => o.id === tenantInMarket.pipedriveBrokerageId)), [tenantInMarket]).get();
    const validationSchema = useTenantInMarketValidationSchema();
    const rowClassName = useRowClassName();

    const close = () => navigate("/");
    const cancel = () => navigate(`/tenantsInMarket/${tenantInMarket!.id}`);
    useClosable(ClosableOrder.Medium, cancel);

    const formik = useFormik<ITenantInMarketState>({
        initialValues: TenantInMarketState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            await updateTenantInMarket(tenantInMarket!, values);
            cancel();
        },
    });

    const remove = async () => {
        await deleteTenantInMarket(tenantInMarket!.id);
        close();
    }

    useEffect(() => {
        mapShapeStore.clear();
    }, [])

    useEffect(() => {
        if (tenantInMarket) {
            drawMapStore.drawTenantInMarket(tenantInMarket.shape);
            formik.resetForm({
                values: {
                    description: tenantInMarket.description,
                    pipedriveCompanyName: companyPipedriveOrganization?.name || null,
                    pipedriveBrokerageName: brokeragePipedriveOrganization?.name || null,
                    landAreaMin: tenantInMarket.landAreaMin,
                    landAreaMax: tenantInMarket.landAreaMax,
                    buildingAreaMin: tenantInMarket.buildingAreaMin,
                    buildingAreaMax: tenantInMarket.buildingAreaMax,
                    notes: tenantInMarket.notes,
                    useType: tenantInMarket.useType,
                    contact: tenantInMarket.contact,
                    targetAnnualRent: tenantInMarket.targetAnnualRent,
                    zoningRequired: tenantInMarket.zoningRequired,
                    siteConditions: tenantInMarket.siteConditions,
                    timing: tenantInMarket.timing,
                    companyWebsite: tenantInMarket.companyWebsite,
                    corpOwnership: tenantInMarket.corpOwnership,
                    revenue: tenantInMarket.revenue,
                    numberOfEmployees: tenantInMarket.numberOfEmployees,
                    numberOfLocations: tenantInMarket.numberOfLocations,
                    inactiveReason: tenantInMarket.inactiveReason,
                    assignedToPipedriveUserId: tenantInMarket.assignedToPipedriveUserId,
                },
            });
        }

        return () => drawMapStore.clear();
    }, [tenantInMarket, companyPipedriveOrganization, brokeragePipedriveOrganization]);

    if (tenantInMarketStore.isTenantsInMarketLoading.fetching)
        return <OnMapSpinner />;

    if (!tenantInMarket)
        return null;

    return (
        <DetailsPanel type={DetailsType.TenantInMarketEdit} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <TenantInMarketIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>{tenantInMarket.description}</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <TenantInMarketFields formik={formik}>
                    {formik.values.inactiveReason && (
                        <div className={rowClassName}>
                            <div className={editStyles.rowGroup}>
                                <div className={rowClassName}>
                                    <StringInput formik={formik} label="INACTIVE REASON" name="inactiveReason" />
                                </div>
                            </div>
                        </div>
                    )}
                </TenantInMarketFields>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <EditControls
                        isStateChanged={formik.dirty || drawMapStore.isResultChanged}
                        isUpdateLoading={tenantInMarketStore.isTenantsInMarketLoading.updating! || pipedriveStore.isOrganizationsLoading.creating!}
                        onUpdate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}>
                    {!formik.values.inactiveReason && (
                        <MarkInactiveControl
                            entityName="Tenant in Market"
                            onMarkInactive={reason => formik.setFieldValue("inactiveReason", reason)}
                        />
                    )}
                    <DeleteControl
                        entityName="Tenant in Market"
                        isLoading={tenantInMarketStore.isTenantsInMarketLoading.deleting!}
                        onDelete={remove}
                    />
                </div>
            </div>
        </DetailsPanel>
    );
}

export default observer(TenantInMarketEdit);